import React from "react";
import { Box, Typography, Chip, Divider, IconButton } from "@mui/material";
import TrainIcon from "@mui/icons-material/Train";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import FaxIcon from "@mui/icons-material/Fax";
import PlaceIcon from "@mui/icons-material/Place";
import { useTranslation } from "react-i18next";
import BgTitle from "components/title/BgTitle";
import { officeMapEmbedUrl } from "config/maps";

export default () => {
  const { t } = useTranslation();

  const statistics = [
    {
      station: t("company_location.nearest_stations.station_1.station"),
      time: t("company_location.nearest_stations.station_1.time"),
      exit: t("company_location.nearest_stations.station_1.exit"),
    },
    {
      station: t("company_location.nearest_stations.station_2.station"),
      time: t("company_location.nearest_stations.station_2.time"),
      exit: t("company_location.nearest_stations.station_2.exit"),
    },
    {
      station: t("company_location.nearest_stations.station_3.station"),
      time: t("company_location.nearest_stations.station_3.time"),
      exit: t("company_location.nearest_stations.station_3.exit"),
    },
    {
      station: t("company_location.nearest_stations.station_4.station"),
      time: t("company_location.nearest_stations.station_4.time"),
      exit: t("company_location.nearest_stations.station_4.exit"),
    },
  ];

  return (
    <Box sx={{ minHeight: "100vh", mt: 2 }}>
      {/* Sticky Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          borderBottom: "1px solid",
          borderColor: "divider",
          px: 2,
          py: 2,
        }}
      >
        <BgTitle isCenter={true} text={t("company_location.heading")} />
      </Box>

      {/* Map Section */}
      <Box sx={{ position: "relative", height: "35vh", mb: 2 }}>
        <iframe
          title="Company Location Map"
          src={officeMapEmbedUrl()}
          style={{ border: 0, width: "100%", height: "100%" }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
        <Chip
          icon={<PlaceIcon sx={{ color: "primary.main" }} />}
          label="地図を開く"
          onClick={() => {}}
          sx={{
            position: "absolute",
            bottom: 16,
            right: 16,
            bgcolor: "white",
            boxShadow: 2,
          }}
        />
      </Box>

      {/* Content Sections */}
      <Box sx={{ py: 2 }}>
        {/* Company Info Section */}
        <Box sx={{ mb: 4 }}>
          {/* Address Info */}
          <Box
            sx={{ mb: 3, borderLeft: "4px solid", borderColor: "primary.main" }}
          >
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                whiteSpace: "pre-line",
                lineHeight: 1.6,
                mb: 2,
                p: 1,
                bgcolor: "#f5f5f5",
                fontSize: "0.8rem",
              }}
            >
              {t("company_location.address.title")}
              {"\n"}
              {t("company_location.address.address_line1")}
              {"\n"}
              {t("company_location.address.address_line2")}
              {"\n"}
              {t("company_location.address.address_line3")}
            </Typography>
          </Box>

          {/* Contact Actions */}
          <Box sx={{ mb: 4 }}>
            {/* Phone */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                py: 1,
                "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
              }}
            >
              <IconButton
                sx={{
                  mr: 2,
                  color: "primary.main",
                  bgcolor: "primary.lighter",
                }}
              >
                <PhoneIcon />
              </IconButton>
              <Typography fontSize={"0.8rem"}>
                {t("company_location.address.phone")}
              </Typography>
            </Box>

            {/* Fax */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                py: 1,
                "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
              }}
            >
              <IconButton
                sx={{
                  mr: 2,
                  color: "primary.main",
                  bgcolor: "primary.lighter",
                }}
              >
                <FaxIcon />
              </IconButton>
              <Typography fontSize={"0.8rem"}>
                {t("company_location.address.fax")}
              </Typography>
            </Box>

            {/* Email */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                py: 1,
                "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
              }}
            >
              <IconButton
                sx={{
                  mr: 2,
                  color: "primary.main",
                  bgcolor: "primary.lighter",
                }}
              >
                <EmailIcon />
              </IconButton>
              <Box>
                <Typography sx={{ mb: 0.5, fontSize: "0.8rem" }}>
                  {t("company_location.address.email_soumu")}
                </Typography>
                <Typography fontSize={"0.8rem"}>
                  {t("company_location.address.email_sales")}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Station Information */}
        <Box sx={{ mt: 2 }}>
          {statistics.map((stat, index) => (
            <Box
              key={index}
              sx={{
                py: 2.5,
                "&:not(:last-child)": {
                  borderBottom: "1px solid",
                  borderColor: "divider",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  position: "relative", // Add positioning context
                }}
              >
                {/* Train Icon - Now absolutely positioned */}
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    backgroundColor: "primary.lighter",
                  }}
                >
                  <TrainIcon
                    sx={{
                      color: "primary.main",
                      fontSize: "1.5rem",
                    }}
                  />
                </Box>

                {/* Content Container - With consistent left margin */}
                <Box
                  sx={{
                    flex: 1,
                    pl: 6, // 40px (icon width) + 8px (spacing)
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.8rem",
                      lineHeight: 1.2,
                      color: "text.primary",
                      mb: 0.5,
                    }}
                  >
                    {stat.station}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        bgcolor: "grey.50",
                        py: 0.5,

                        borderRadius: "full",
                      }}
                    >
                      <AccessTimeIcon
                        sx={{
                          fontSize: "0.875rem",
                          color: "text.secondary",
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontWeight: 500,
                          fontSize: "0.7rem",
                        }}
                      >
                        {stat.time}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <DirectionsWalkIcon
                        sx={{
                          fontSize: "0.7rem",
                          color: "text.secondary",
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontSize: "0.7rem",
                        }}
                      >
                        {stat.exit}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
